/**
 * Custom Quest Manager
 * Handles runtime quest creation, editing, and persistence
 * Stores custom quests in localStorage and merges with default quests
 */

import { QuestDefinition, GameEvent } from '@/types';
import { eventBus } from '@/services/EventBus';

const CUSTOM_QUESTS_STORAGE_KEY = 'custom_quests';

export class CustomQuestManager {
  private customQuests: Map<string, QuestDefinition> = new Map();

  /**
   * Load custom quests from localStorage
   */
  load(): void {
    try {
      const stored = localStorage.getItem(CUSTOM_QUESTS_STORAGE_KEY);
      if (stored) {
        const quests: QuestDefinition[] = JSON.parse(stored);
        this.customQuests.clear();
        quests.forEach((quest) => {
          this.customQuests.set(quest.id, quest);
        });
        console.log(`[CustomQuestManager] Loaded ${quests.length} custom quests`);
      }
    } catch (error) {
      console.error('[CustomQuestManager] Failed to load custom quests:', error);
      this.customQuests.clear();
    }
  }

  /**
   * Save custom quests to localStorage
   */
  save(): void {
    try {
      const quests = Array.from(this.customQuests.values());
      localStorage.setItem(CUSTOM_QUESTS_STORAGE_KEY, JSON.stringify(quests, null, 2));
      console.log(`[CustomQuestManager] Saved ${quests.length} custom quests`);
    } catch (error) {
      console.error('[CustomQuestManager] Failed to save custom quests:', error);
    }
  }

  /**
   * Get all custom quests
   */
  getAllCustomQuests(): QuestDefinition[] {
    return Array.from(this.customQuests.values());
  }

  /**
   * Get a specific custom quest by ID
   */
  getCustomQuest(questId: string): QuestDefinition | undefined {
    return this.customQuests.get(questId);
  }

  /**
   * Check if a quest is custom (vs default)
   */
  isCustomQuest(questId: string): boolean {
    return this.customQuests.has(questId);
  }

  /**
   * Create or update a custom quest
   */
  saveQuest(quest: QuestDefinition): void {
    this.customQuests.set(quest.id, quest);
    this.save();
    console.log(`[CustomQuestManager] Saved quest: ${quest.id}`);

    // Emit event for auto-refresh
    eventBus.emit(GameEvent.CUSTOM_QUEST_UPDATED, { questId: quest.id });
  }

  /**
   * Delete a custom quest
   */
  deleteQuest(questId: string): boolean {
    if (this.customQuests.has(questId)) {
      this.customQuests.delete(questId);
      this.save();
      console.log(`[CustomQuestManager] Deleted quest: ${questId}`);
      return true;
    }
    return false;
  }

  /**
   * Validate quest definition
   * Returns array of validation errors (empty if valid)
   */
  validateQuest(quest: QuestDefinition): string[] {
    const errors: string[] = [];

    // Required fields
    if (!quest.id || quest.id.trim() === '') {
      errors.push('Quest ID is required');
    }
    if (!quest.name || quest.name.trim() === '') {
      errors.push('Quest name is required');
    }
    if (!quest.description || quest.description.trim() === '') {
      errors.push('Quest description is required');
    }

    // Objectives
    if (!quest.objectives || quest.objectives.length === 0) {
      errors.push('At least one objective is required');
    } else {
      quest.objectives.forEach((obj, idx) => {
        if (!obj.id || obj.id.trim() === '') {
          errors.push(`Objective ${idx + 1}: ID is required`);
        }
        if (!obj.type) {
          errors.push(`Objective ${idx + 1}: Type is required`);
        }
        if (!obj.description || obj.description.trim() === '') {
          errors.push(`Objective ${idx + 1}: Description is required`);
        }
        if (obj.targetValue === undefined || obj.targetValue <= 0) {
          errors.push(`Objective ${idx + 1}: Target value must be greater than 0`);
        }
      });
    }

    // Rewards
    if (!quest.rewards || quest.rewards.length === 0) {
      errors.push('At least one reward is required');
    } else {
      quest.rewards.forEach((reward, idx) => {
        if (!reward.type) {
          errors.push(`Reward ${idx + 1}: Type is required`);
        }
        if (reward.value === undefined) {
          errors.push(`Reward ${idx + 1}: Value is required`);
        }
      });
    }

    return errors;
  }

  /**
   * Generate a unique quest ID
   */
  generateQuestId(baseName: string): string {
    const base = baseName.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    let id = `custom_${base}`;
    let counter = 1;

    while (this.customQuests.has(id)) {
      id = `custom_${base}_${counter}`;
      counter++;
    }

    return id;
  }

  /**
   * Export quest as TypeScript code
   */
  exportQuestAsTypeScript(quest: QuestDefinition): string {
    return `export const ${quest.id.toUpperCase()}: QuestDefinition = ${JSON.stringify(
      quest,
      null,
      2
    )};`;
  }

  /**
   * Export all custom quests as TypeScript code
   */
  exportAllQuestsAsTypeScript(): string {
    const quests = this.getAllCustomQuests();
    return quests.map((quest) => this.exportQuestAsTypeScript(quest)).join('\n\n');
  }

  /**
   * Assign quest to a chain
   */
  assignQuestToChain(questId: string, chainId: string | null): void {
    const quest = this.customQuests.get(questId);
    if (!quest) {
      console.warn(`[CustomQuestManager] Cannot assign non-existent quest to chain: ${questId}`);
      return;
    }

    quest.chainId = chainId || undefined;
    this.saveQuest(quest);
    console.log(`[CustomQuestManager] Assigned quest ${questId} to chain ${chainId || 'uncategorized'}`);
  }

  /**
   * Update quest display order
   */
  updateQuestOrder(questId: string, displayOrder: number): void {
    const quest = this.customQuests.get(questId);
    if (!quest) {
      console.warn(`[CustomQuestManager] Cannot update order of non-existent quest: ${questId}`);
      return;
    }

    quest.displayOrder = displayOrder;
    this.saveQuest(quest);
  }

  /**
   * Get quests by chain ID
   * @param chainId - Chain ID or null for uncategorized quests
   */
  getQuestsByChain(chainId: string | null): QuestDefinition[] {
    return Array.from(this.customQuests.values())
      .filter(q => (chainId ? q.chainId === chainId : !q.chainId))
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  }

  /**
   * Clear all custom quests (use with caution!)
   */
  clearAll(): void {
    this.customQuests.clear();
    this.save();
    console.log('[CustomQuestManager] Cleared all custom quests');
  }
}

// Singleton instance
export const customQuestManager = new CustomQuestManager();
